import { Close, Save } from '@mui/icons-material';
import { Box, Button, Divider, List, ListItem, Typography } from '@mui/material';
import { deepEqual } from 'fast-equals';
import { useSnackbar } from 'notistack';
import { FC, useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { useHistory, useParams } from 'react-router-dom';
import { Card, Loader, Page, PageTitleWithBack } from '../../../../../components';
import {
  getNeighborhoodSeriesStandardFeatures,
  getNeighborhoodSeriesStandardFeaturesOptions,
  updateNeighborhoodSeriesStandardFeatures
} from '../../../../../fetch';
import { IStandardFeaturesOptions } from '../../../../../models';
import { StandardFeaturesAlwaysIncluded } from './standard-feature-always-included';
import { StandardFeaturesDropdown } from './standard-feature-dropdown';
import { StandardFeaturesStandaloneFeature } from './standard-feature-standalone';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';

export const StandardFeaturesPage: FC = () => {
  const classes = useStyles();
  const { neighborhoodId, selectedSeriesId }: { neighborhoodId: string; selectedSeriesId: string } =
    useParams();
  const history = useHistory();
  const [selectedFeatures, setSelectedFeatures] = useState<number[]>([]);

  const { isLoading: isLoadingSeriesStandardFeatures, data: neighborhoodsStandardFeatures } =
    useQuery<number[], Error>(
      ['neighborhoodsStandardFeatures'],
      () => getNeighborhoodSeriesStandardFeatures(neighborhoodId, selectedSeriesId),
      {
        onSuccess: data => {
          setSelectedFeatures(data);
        },
        notifyOnChangeProps: 'tracked'
      }
    );

  const [featureSetTotal, setFeatureSetTotal] = useState(Infinity);

  const {
    isLoading: isLoadingSeriesStandardFeaturesOptions,
    data: neighborhoodsStandardFeaturesOptions
  } = useQuery<IStandardFeaturesOptions, Error>(
    ['neighborhoodsStandardFeaturesOptions'],
    () => getNeighborhoodSeriesStandardFeaturesOptions(),
    {
      onSuccess: data => {
        setFeatureSetTotal(data.featureSets.flatMap(feature => feature.sets).length);
      },
      notifyOnChangeProps: 'tracked'
    }
  );

  const [standAloneTotalSelected, setStandAloneTotalSelected] = useState(0);
  useEffect(() => {
    const standAloneItems = neighborhoodsStandardFeaturesOptions?.standAlone?.flatMap(feature =>
      feature.sets.flatMap(set => set.options.map(option => option.id))
    );
    const totalNumberSelectedFromStandAlone = selectedFeatures?.reduce(
      (acc, cur) =>
        standAloneItems?.includes(cur)
          ? Object.assign(acc, { amount: acc?.amount + 1 })
          : Object.assign(acc, { res: acc?.res?.concat(cur) }),
      { amount: 0, res: [] }
    );
    setStandAloneTotalSelected(totalNumberSelectedFromStandAlone?.amount);
  }, [selectedFeatures, neighborhoodsStandardFeaturesOptions]);

  const handleChange = value => {
    selectedFeatures?.includes(value)
      ? setSelectedFeatures(prev => prev.filter(featureId => featureId !== value))
      : setSelectedFeatures(prev => [...prev, value]);
  };

  const handleNavigateBack = () => {
    history.push(`/development/neighborhoods/${neighborhoodId}/options/${selectedSeriesId}`);
  };

  const { enqueueSnackbar } = useSnackbar();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleSave = async () => {
    try {
      setIsSubmitting(true);
      await updateNeighborhoodSeriesStandardFeatures(
        neighborhoodId,
        selectedSeriesId,
        selectedFeatures
      );

      enqueueSnackbar(`Standard Features Updated!`, {
        variant: 'success'
      });
      handleNavigateBack();
    } catch (error) {
      enqueueSnackbar(`${error}`, {
        variant: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isSaveDisabled = () => {
    if (deepEqual(neighborhoodsStandardFeatures, selectedFeatures)) return true;
    if (featureSetTotal > selectedFeatures?.length - standAloneTotalSelected) return true;
    if (isLoadingSeriesStandardFeatures || isLoadingSeriesStandardFeaturesOptions) return true;
    return false;
  };

  return (
    <Page title="Options - Standard Features">
      <PageTitleWithBack
        title="Standard Features"
        backText={'Neighborhood Options'}
        backUrl={`/development/neighborhoods/${neighborhoodId}/options/${selectedSeriesId}`}
        subTitle={
          <>
            <Typography variant={'subtitle1'}>
              Please select the standard features for this Neighborhood Series
            </Typography>
          </>
        }
      />
      {(isLoadingSeriesStandardFeatures ||
        isLoadingSeriesStandardFeaturesOptions ||
        isSubmitting) && <Loader type="fullscreen" position="centered" />}
      {!isLoadingSeriesStandardFeatures && !isLoadingSeriesStandardFeaturesOptions && (
        <>
          <Box
            margin={{
              sm: '0 0 0 1rem'
            }}
          >
            <Typography className={classes.header} variant="h5">
              Set Features - Please choose one item per set
            </Typography>
            {neighborhoodsStandardFeaturesOptions?.featureSets.map(feature => {
              return (
                <>
                  <Card title={feature.phaseName}>
                    {feature.sets.map(set => {
                      return (
                        <>
                          <StandardFeaturesDropdown
                            handleChange={handleChange}
                            name={set.optionSetName}
                            options={set.options}
                            isLoading={
                              isLoadingSeriesStandardFeatures ||
                              isLoadingSeriesStandardFeaturesOptions
                            }
                            selected={selectedFeatures}
                          />
                        </>
                      );
                    })}
                  </Card>
                </>
              );
            })}
            <Divider />

            <Typography className={classes.header} variant="h5">
              Standalone Features - Always Included
            </Typography>
            {neighborhoodsStandardFeaturesOptions?.alwaysIncluded.map(feature => {
              return (
                <>
                  <Card title={feature.phaseName}>
                    {feature.sets.map(set => {
                      return (
                        <>
                          <List>
                            {set.options.map(option => (
                              <ListItem key={option.name} disablePadding>
                                <StandardFeaturesAlwaysIncluded name={option.name} />
                              </ListItem>
                            ))}
                          </List>
                        </>
                      );
                    })}
                  </Card>
                </>
              );
            })}
            <Divider />

            <Typography className={classes.header} variant="h5">
              Standalone Features - Please choose to include or not
            </Typography>
            {neighborhoodsStandardFeaturesOptions?.standAlone.map(feature => {
              return (
                <>
                  <Card title={feature.phaseName}>
                    {feature.sets.map(set => {
                      return (
                        <>
                          {set.options.map(option => (
                            <StandardFeaturesStandaloneFeature
                              key={option.name}
                              handleChange={handleChange}
                              name={option.name}
                              id={option.id}
                              isLoading={
                                isLoadingSeriesStandardFeatures ||
                                isLoadingSeriesStandardFeaturesOptions
                              }
                              selected={selectedFeatures}
                            />
                          ))}
                        </>
                      );
                    })}
                  </Card>
                </>
              );
            })}
          </Box>
          <Box marginTop="2rem">
            <Button
              disabled={isSaveDisabled()}
              startIcon={<Save />}
              variant="contained"
              color="primary"
              onClick={() => handleSave()}
            >
              Save
            </Button>
            <Button
              type="button"
              variant="contained"
              startIcon={<Close />}
              color="inherit"
              onClick={() => {
                if (!deepEqual(neighborhoodsStandardFeatures, selectedFeatures)) {
                  const result = window.confirm(
                    'You have unsaved changes, are you sure you want to exit?'
                  );
                  if (result) {
                    handleNavigateBack();
                  } else {
                    return;
                  }
                } else {
                  handleNavigateBack();
                }
              }}
            >
              Cancel
            </Button>
          </Box>
        </>
      )}
    </Page>
  );
};
const useStyles = makeStyles((theme: Theme) => ({
  header: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    color: theme.palette.secondary.main
  }
}));
